import { toSafeBrandColor } from '@common/theme-layer/utils/brand-color'
import type { BrandTokens } from '@common/theme-layer/composables/useTheme'

import type { LocationBranding } from '../types/location'

export const DEFAULT_PORTAL_BRAND_TOKENS = {
  primary: '#004eeb',
  tertiary: '#004eeb',
  background: '#ffffff',
  surface: '#f5f6f8',
  error: '#d92d20',
  fontColor: '#1d2939',
  sidebar: '#ffffff',
  border: '#eaecf0',
} as const satisfies BrandTokens

/**
 * Maps clientportal portal branding to the partial BrandTokens accepted by
 * `useTheme().setBrandTokens`.
 *
 * - `brandColor1` → `primary`: drives the primary scale (50-900), the HighRise
 *   `--primary-*` bridge, and `--cp-text-on-primary`.
 * - `brandColor2` → `secondary`: drives the secondary scale (`--cp-secondary-*`) +
 *   `--cp-text-on-secondary`, and writes `--cp-brand-secondary` — the same var the
 *   legacy `applyBranding` path also sets from `brandColor2`, so they agree.
 *
 * This function returns only sparse brand overrides. Call
 * `resolvePortalBrandTokens` when a complete portal-owned set is required.
 * Each colour is mapped independently — a blank/absent value is simply skipped.
 */
export function mapPortalBrandingToTokens(branding: Pick<LocationBranding, 'brandColor1' | 'brandColor2'>): Partial<BrandTokens> {
  const tokens: Partial<BrandTokens> = {}
  const primary = toSafeBrandColor(branding.brandColor1)
  const secondary = toSafeBrandColor(branding.brandColor2)

  if (primary) {
    tokens.primary = primary
  }

  if (secondary) {
    tokens.secondary = secondary
  }

  return tokens
}

/**
 * Resolves a complete portal-owned token set so restoring the portal cannot
 * merge brand colours with stale community chrome. `overrides` (an AI-built
 * portal's customization colours) win over the brandColor-derived tokens.
 */
export function resolvePortalBrandTokens(
  branding: Pick<LocationBranding, 'brandColor1' | 'brandColor2'>,
  overrides: Partial<BrandTokens> = {}
): BrandTokens | null {
  const brandingTokens = { ...mapPortalBrandingToTokens(branding), ...overrides }

  if (!brandingTokens.primary) {
    return null
  }

  return {
    ...DEFAULT_PORTAL_BRAND_TOKENS,
    ...brandingTokens,
  }
}
